import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import './PasswordConfirmModal.css';

const PasswordConfirmModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await onConfirm(password);

      if (result.success) {
        setPassword('');
        setError('');
        onClose(true);
      } else {
        setError(result.error || 'Invalid password');
      }
    } catch (err) {
      setError('Failed to verify password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose(false);
  };

  return (
    <div className="password-confirm-overlay" onClick={handleClose}>
      <div className="password-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose}>
          <X size={20} />
        </button>

        <div className="modal-icon">
          <Lock size={48} />
        </div>

        <h2 className="modal-title">{title || 'Confirm Your Password'}</h2>
        <p className="modal-message">
          {message || 'Please enter your password to view sensitive information'}
        </p>

        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter your password"
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="confirm-btn"
              disabled={loading || !password}
            >
              {loading ? 'Verifying...' : 'Confirm'}
            </button>
          </div>
        </form>

        <div className="security-notice">
          <Lock size={14} />
          <span>Your password is encrypted and never stored</span>
        </div>
      </div>
    </div>
  );
};

export default PasswordConfirmModal;
